import { useEffect, useState, useCallback } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

// =============================================================================
// Types
// =============================================================================

interface CursorState {
  isHovering: boolean;
  isClicking: boolean;
  isVisible: boolean;
}

// =============================================================================
// Constants
// =============================================================================

// Brand colors
const COLORS = {
  primary: "#DCA0FA", // Plasma Violet
  accent: "#E6BEFA", // Aurora Lilac
};

// Interactive element selectors
const INTERACTIVE_SELECTORS = [
  "a",
  "button",
  "[role='button']",
  "input",
  "textarea",
  "select",
  "[tabindex]:not([tabindex='-1'])",
  "[data-cursor-hover]",
].join(", ");

// =============================================================================
// Hook: useIsTouchDevice
// =============================================================================

function useIsTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    // Check for touch capability
    const checkTouch = () => {
      const hasTouch =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-expect-error - msMaxTouchPoints is IE-specific
        navigator.msMaxTouchPoints > 0;

      // Also check for coarse pointer (typical of touch devices)
      const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;

      setIsTouch(hasTouch && hasCoarsePointer);
    };

    checkTouch();

    // Re-check on resize (for devices that can switch modes)
    window.addEventListener("resize", checkTouch);
    return () => window.removeEventListener("resize", checkTouch);
  }, []);

  return isTouch;
}

// =============================================================================
// CustomCursor Component
// =============================================================================

export function CustomCursor() {
  const isTouchDevice = useIsTouchDevice();
  const [cursorState, setCursorState] = useState<CursorState>({
    isHovering: false,
    isClicking: false,
    isVisible: false,
  });

  // Motion values for cursor position
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);

  // Spring configs for smooth animation
  // Dot follows more tightly
  const dotSpringConfig = { damping: 30, stiffness: 500, mass: 0.5 };
  // Ring follows with more delay for that trailing effect
  const ringSpringConfig = { damping: 20, stiffness: 150, mass: 0.8 };

  // Spring-animated positions
  const dotX = useSpring(cursorX, dotSpringConfig);
  const dotY = useSpring(cursorY, dotSpringConfig);
  const ringX = useSpring(cursorX, ringSpringConfig);
  const ringY = useSpring(cursorY, ringSpringConfig);

  // Handle mouse movement
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);

      if (!cursorState.isVisible) {
        setCursorState((prev) => ({ ...prev, isVisible: true }));
      }
    },
    [cursorX, cursorY, cursorState.isVisible]
  );

  // Handle mouse enter/leave window
  const handleMouseEnter = useCallback(() => {
    setCursorState((prev) => ({ ...prev, isVisible: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setCursorState((prev) => ({ ...prev, isVisible: false }));
  }, []);

  // Handle mouse down/up
  const handleMouseDown = useCallback(() => {
    setCursorState((prev) => ({ ...prev, isClicking: true }));
  }, []);

  const handleMouseUp = useCallback(() => {
    setCursorState((prev) => ({ ...prev, isClicking: false }));
  }, []);

  // Handle hover over interactive elements
  const handleElementHover = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const isInteractive = target.closest(INTERACTIVE_SELECTORS);
    setCursorState((prev) => ({ ...prev, isHovering: !!isInteractive }));
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (isTouchDevice) return;

    // Hide default cursor
    document.body.style.cursor = "none";

    // Add event listeners
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mousemove", handleElementHover);
    document.addEventListener("mouseenter", handleMouseEnter);
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      // Restore default cursor
      document.body.style.cursor = "";

      // Remove event listeners
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mousemove", handleElementHover);
      document.removeEventListener("mouseenter", handleMouseEnter);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isTouchDevice,
    handleMouseMove,
    handleElementHover,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
  ]);

  // Don't render on touch devices
  if (isTouchDevice) {
    return null;
  }

  const { isHovering, isClicking, isVisible } = cursorState;

  // Calculate sizes based on state
  const dotSize = isClicking ? 6 : isHovering ? 10 : 8;
  const ringSize = isClicking ? 32 : isHovering ? 48 : 36;

  // Calculate colors based on state
  const dotColor = isHovering ? COLORS.accent : COLORS.primary;
  const ringBorderColor = isHovering ? COLORS.accent : COLORS.primary;
  const ringBorderWidth = isHovering ? 2 : 1;

  return (
    <>
      {/* Cursor Dot */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference"
        style={{
          x: dotX,
          y: dotY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          width: dotSize,
          height: dotSize,
          opacity: isVisible ? 1 : 0,
          backgroundColor: dotColor,
        }}
        transition={{
          width: { type: "spring", stiffness: 400, damping: 25 },
          height: { type: "spring", stiffness: 400, damping: 25 },
          opacity: { duration: 0.15 },
          backgroundColor: { duration: 0.2 },
        }}
        aria-hidden="true"
        data-cursor-dot
      />

      {/* Cursor Ring */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9998] rounded-full"
        style={{
          x: ringX,
          y: ringY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          width: ringSize,
          height: ringSize,
          opacity: isVisible ? (isHovering ? 0.9 : 0.6) : 0,
          borderColor: ringBorderColor,
          borderWidth: ringBorderWidth,
          scale: isClicking ? 0.9 : 1,
        }}
        transition={{
          width: { type: "spring", stiffness: 300, damping: 20 },
          height: { type: "spring", stiffness: 300, damping: 20 },
          opacity: { duration: 0.2 },
          borderColor: { duration: 0.2 },
          scale: { type: "spring", stiffness: 400, damping: 20 },
        }}
        aria-hidden="true"
        data-cursor-ring
      />

      {/* Glow effect on hover */}
      {isHovering && (
        <motion.div
          className="fixed top-0 left-0 pointer-events-none z-[9997] rounded-full"
          style={{
            x: ringX,
            y: ringY,
            translateX: "-50%",
            translateY: "-50%",
            background: `radial-gradient(circle, ${COLORS.accent}25 0%, transparent 70%)`,
          }}
          initial={{ width: 0, height: 0, opacity: 0 }}
          animate={{
            width: 80,
            height: 80,
            opacity: 0.6,
          }}
          exit={{ width: 0, height: 0, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 20,
          }}
          aria-hidden="true"
          data-cursor-glow
        />
      )}

      {/* Trail particles when clicking */}
      {isClicking && (
        <motion.div
          className="fixed top-0 left-0 pointer-events-none z-[9996]"
          style={{
            x: dotX,
            y: dotY,
            translateX: "-50%",
            translateY: "-50%",
          }}
          initial={{ scale: 0.5, opacity: 1 }}
          animate={{ scale: 2, opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div
            className="w-4 h-4 rounded-full"
            style={{
              background: `radial-gradient(circle, ${COLORS.primary}60 0%, transparent 70%)`,
            }}
          />
        </motion.div>
      )}
    </>
  );
}

export default CustomCursor;
